import { useCallback, useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { format } from 'date-fns';
// @mui
import {
  Button,
  TextField,
  Grid,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import LoadingButton from '@mui/lab/LoadingButton';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
// utils
import { fData } from 'src/utils/format/format-number';
// redux
import { useDispatch } from 'react-redux';
import { updateReceivedCheckInRib } from 'src/redux/slices/dataSlice';
// components
import { useSnackbar } from 'src/components/snackbar';
import {
  RHFUploadAvatar,
} from 'src/components/hook-form';
import { Company, ReceivedCheck, Rib } from 'src/types/data';
import { formatRib } from 'src/utils/format/format_rib';
import { useServerRequest } from 'src/hooks/useServerRequest';
import { HOST_ROOT } from 'src/config-global';
import Label, { LabelColor } from 'src/components/label';
import { useLocales } from 'src/locales';

// ----------------------------------------------------------------------

type Props = {
  ch_e_ck: ReceivedCheck | null;
  comp_any: Company | null;
  r_i_b: Rib | null;
  canEdit: boolean;
};

export default function CheckSetting({ ch_e_ck, comp_any, r_i_b, canEdit }: Props) {
  const [beneficiary, setBeneficiary] = useState<string | null>(ch_e_ck?.beneficiere ?? null);
  const [amount, setAmount] = useState<string | null>(ch_e_ck?.amount ?? null);
  const [takenDate, setTakenDate] = useState<Date | null>(
    ch_e_ck?.taken_date ? new Date(ch_e_ck.taken_date) : null
  );
  const [dueDate, setDueDate] = useState<Date | null>(
    ch_e_ck?.due_date ? new Date(ch_e_ck.due_date) : null
  );
  const [bill, setBill] = useState<string | null>(ch_e_ck?.facture ?? null);
  const [note, setNote] = useState<string | null>(ch_e_ck?.note ?? null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusText, setStatusText] = useState<string>('Unknowen');
  const [statusColor, setStatusColor] = useState<LabelColor | undefined>(undefined);
  const { t } = useLocales();
  const { enqueueSnackbar } = useSnackbar()
  const { makeRequest } = useServerRequest();
  const methods = useForm({
    defaultValues: {
      photoURL: ch_e_ck?.photo
        ? { preview: `${HOST_ROOT}/storage/${ch_e_ck?.photo}` }
        : null,
    },
  });
  const dispatch = useDispatch();
  const { setValue } = methods;

  // Update the statusText using useEffect
  useEffect(() => {
    switch (ch_e_ck?.status) {
      case 6:
        setStatusText(t('common.status.c'));
        setStatusColor('error');
        break;
      case 5:
        setStatusText(t('common.status.p'));
        setStatusColor('success');
        break;
      case 3:
        setStatusText(t('common.status.d'));
        setStatusColor('warning');
        break;
      case 4:
        setStatusText(t('common.status.u'));
        setStatusColor('secondary');
        break;
      default:
        setStatusText(t('common.status.circ'));
        setStatusColor('info');
    }
  }, [ch_e_ck?.status, t]);


  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!canEdit) {
        enqueueSnackbar(t('common.alert.permission'), { variant: 'error', key: new Date().getTime() });
        return;
      }

      const file = acceptedFiles[0];

      if (file && ch_e_ck) {
        const data = new FormData();
        data.append('file', file);
        data.append('type', '2');
        const response = await makeRequest(`/updateCheckFile/${ch_e_ck.id}`, {
          method: 'POST',
          data,
        }, '-');

        if (response.success) {
          enqueueSnackbar(t('common.alert.file'), { variant: 'success', key: new Date().getTime() });
          // Create a preview URL for the file
          const newFile = Object.assign(file, {
            preview: URL.createObjectURL(file),
          });
          setValue('photoURL', newFile, { shouldValidate: true });
        }
      }
    },
    [setValue, ch_e_ck, enqueueSnackbar, makeRequest, canEdit, t]
  );

  const handleSubmits = async () => {

    if (!canEdit) {
      enqueueSnackbar(t('common.alert.permission'), { variant: 'error', key: new Date().getTime() });
      return;
    }
    if (!ch_e_ck) return;

    setIsSubmitting(true);
    const formattedTakenDate = takenDate ? format(takenDate, 'yyyy-MM-dd HH:mm:ss') : null;
    const formattedDueDate = dueDate ? format(dueDate, 'yyyy-MM-dd HH:mm:ss') : null;

    const data = {
      check_id: ch_e_ck.id,
      benificier_id: beneficiary,
      amount,
      taken_date: formattedTakenDate,
      due_date: formattedDueDate,
      facture: bill,
      remarque: note,
    };

    let error = false;

    if (!data.benificier_id) {
      enqueueSnackbar(t('common.input.benef.error1'), { variant: 'error', key: new Date().getTime() });
      error = true;
    }
    if (!data.amount || Number.isNaN(Number(data.amount))) {
      enqueueSnackbar(t('common.input.amount.error1'), { variant: 'error', key: new Date().getTime() });
      error = true;
    }
    if (!data.taken_date) {
      enqueueSnackbar(t('common.input.takenDate.error1'), { variant: 'error', key: new Date().getTime() });
      error = true;
    }
    if (error) {
      setIsSubmitting(false);
      return;
    }
    const response = await makeRequest(`/updateCheckRecu`, {
      method: 'POST',
      data,
    }, t('common.alert.checkUpdate'));

    if (response.success && response.data) {
      const newCheck: ReceivedCheck = {
        ...ch_e_ck,
        ...response.data.check
      };
      dispatch(
        updateReceivedCheckInRib({
          companyId: comp_any?.id || 0,
          ribId: r_i_b?.id || 0,
          receivedCheck: newCheck,
        })
      );
    }

    setIsSubmitting(false);
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const formattedValue = value.replace(',', '.').replace(/[^0-9.]/g, '');
    if ((formattedValue.match(/\./g) || []).length > 1) {
      return;
    }
    setAmount(formattedValue);
  };

  const handleCancelCheck = async (status: number) => {
    if (!canEdit) {
      enqueueSnackbar(t('common.alert.permission'), { variant: 'error', key: new Date().getTime() });
      return;
    }

    if (!ch_e_ck) return;
    const data = {
      id: ch_e_ck.id,
      status
    };
    const response = await makeRequest(`/changeStatusRecieved`, {
      method: 'POST',
      data,
    }, '-');
    if (response.success) {
      dispatch(
        updateReceivedCheckInRib({
          companyId: comp_any?.id || 0,
          ribId: r_i_b?.id || 0,
          receivedCheck: {
            ...ch_e_ck,
            status,
          },
        })
      );
      switch (status) {
        case 6:
          setStatusText(t('common.status.c'));
          setStatusColor('error');
          enqueueSnackbar(t('common.alert.c.c'), { variant: 'success', key: new Date().getTime() });
          break;
        case 5:
          setStatusText(t('common.status.p'));
          setStatusColor('success');
          enqueueSnackbar(t('common.alert.c.p'), { variant: 'success', key: new Date().getTime() });
          break;
        case 3:
          setStatusText(t('common.status.d'));
          setStatusColor('warning');
          enqueueSnackbar(t('common.alert.c.d'), { variant: 'success', key: new Date().getTime() });
          break;
        case 4:
          setStatusText(t('common.status.u'));
          setStatusColor('secondary');
          enqueueSnackbar(t('common.alert.c.u'), { variant: 'success', key: new Date().getTime() });
          break;
        default:
          setStatusText(t('common.status.circ'));
          setStatusColor('info');
          enqueueSnackbar(t('common.alert.c.circ'), { variant: 'success', key: new Date().getTime() });
          break;
      }
    }
  };

  return (
    <Grid container spacing={3}>
      {!canEdit && <Alert
        severity="info"
        variant="filled"
        sx={{ width: 1 }}
      >
        {t('common.alert.permission')}
      </Alert>}
      <Grid item xs={12} md={4}>
        <FormProvider {...methods}>
          <Card sx={{ pt: 10, pb: 5, px: 3, textAlign: 'center' }}>
            <RHFUploadAvatar
              name="photoURL"
              maxSize={3145728}
              onDrop={handleDrop}
              helperText={
                <Typography
                  variant="caption"
                  sx={{
                    mt: 3,
                    mx: 'auto',
                    display: 'block',
                    textAlign: 'center',
                    color: 'text.disabled',
                  }}
                >
                  {t('accounts.allowed')}
                  <br />{t('accounts.max')} {fData(3145728)}
                </Typography>
              }
            />

            <Typography sx={{ pt: '20px' }}><Label color={statusColor}> {t('roles.status')}: {statusText}</Label></Typography>
            <Button onClick={() => { handleCancelCheck(1) }} variant="soft" color="info" sx={{ mt: 1, ml: 1 }}>
              {t('common.status.circ')}
            </Button>
            <Button onClick={() => { handleCancelCheck(5) }} variant="soft" color="success" sx={{ mt: 1, ml: 1 }}>
              {t('common.status.p')}
            </Button>
            <Button onClick={() => { handleCancelCheck(3) }} variant="soft" color="warning" sx={{ mt: 1, ml: 1 }}>
              {t('common.status.d')}
            </Button>
            <Button onClick={() => { handleCancelCheck(4) }} variant="soft" color="secondary" sx={{ mt: 1, ml: 1 }}>
              {t('common.status.u')}
            </Button>
            <Button onClick={() => { handleCancelCheck(6) }} variant="soft" color="error" sx={{ mt: 1, ml: 1 }}>
              {t('common.status.c')}
            </Button>
          </Card>
        </FormProvider>
      </Grid>

      <Grid item xs={12} md={8}>
        <Card sx={{ p: 3 }}>
          <Grid container spacing={2} sx={{ pt: '10px' }}>
            <Grid item xs={6}>
              <Autocomplete
                disabled
                options={[]}
                renderInput={(params) => <TextField {...params} label={comp_any?.name} />}
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                options={[]}
                disabled
                renderInput={(params) => <TextField {...params} label={`${r_i_b?.bref} - ${r_i_b && formatRib(r_i_b.rib)}`} />}
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                options={[]}
                disabled
                renderInput={(params) => <TextField {...params} label={ch_e_ck?.code} />}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label={t('common.input.benef.label')}
                value={beneficiary || ''}
                onChange={(e) => setBeneficiary(e.target.value.toUpperCase())}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('common.input.amount.label')}
                value={amount || ''}
                onChange={handleAmountChange}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <DatePicker
                label={t('common.input.takenDate.label')}
                value={takenDate || null}
                onChange={(newValue) => setTakenDate(newValue)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>

            <Grid item xs={6} md={3}>
              <DatePicker
                label={t('common.input.dueDate.label')}
                value={dueDate || null}
                onChange={(newValue) => setDueDate(newValue)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('common.input.bill.label')}
                value={bill || ''}
                onChange={(e) => setBill(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('common.input.note.label')}
                multiline
                rows={2}
                value={note || ''}
                onChange={(e) => setNote(e.target.value)}
              />
            </Grid>
          </Grid>

          <Stack spacing={3} alignItems="flex-end" sx={{ mt: 3 }}>
            <LoadingButton onClick={handleSubmits} variant="contained" loading={isSubmitting}>
              {t('common.button.action.saveChanges')}
            </LoadingButton>
          </Stack>
        </Card>
      </Grid>
    </Grid>
  );
}
